import MobileDetect from 'mobile-detect'
import { ofType } from 'redux-observable'
import { of } from 'rxjs'

import { catchError, concatMap, debounceTime, filter, mergeMap, takeUntil } from 'rxjs/operators'

export const ENVIRONMENT_DEVICE_SET = 'ENVIRONMENT_DEVICE_SET'
export const ENVIRONMENT_DEVICE_SET_CANCELED = 'ENVIRONMENT_DEVICE_SET_CANCELED'
export const ENVIRONMENT_DEVICE_SET_ERROR = 'ENVIRONMENT_DEVICE_SET_ERROR'
export const ENVIRONMENT_DEVICE_SET_FULFILLED = 'ENVIRONMENT_DEVICE_SET_FULFILLED'

export const ENVIRONMENT_ONLINE_SET = 'ENVIRONMENT_ONLINE_SET'

export const ENVIRONMENT_SET = 'ENVIRONMENT_SET'
export const ENVIRONMENT_SET_CANCELED = 'ENVIRONMENT_SET_CANCELED'
export const ENVIRONMENT_SET_ERROR = 'ENVIRONMENT_SET_ERROR'
export const ENVIRONMENT_SET_FULFILLED = 'ENVIRONMENT_SET_FULFILLED'

export const ENVIRONMENT_LOCATION_SET = 'ENVIRONMENT_LOCATION_SET'
export const ENVIRONMENT_LOCATION_SET_CANCELED = 'ENVIRONMENT_LOCATION_SET_CANCELED'
export const ENVIRONMENT_LOCATION_SET_ERROR = 'ENVIRONMENT_LOCATION_SET_ERROR'
export const ENVIRONMENT_LOCATION_SET_FULFILLED = 'ENVIRONMENT_LOCATION_SET_FULFILLED'

export const ENVIRONMENT_STAGE_SET = 'ENVIRONMENT_STAGE_SET'
export const ENVIRONMENT_STAGE_SET_CANCELED = 'ENVIRONMENT_STAGE_SET_CANCELED'
export const ENVIRONMENT_STAGE_SET_ERROR = 'ENVIRONMENT_STAGE_SET_ERROR'
export const ENVIRONMENT_STAGE_SET_FULFILLED = 'ENVIRONMENT_STAGE_SET_FULFILLED'

export const ENVIRONMENT_VISIBILITY_SET = 'ENVIRONMENT_VISIBILITY_SET'

export const ENVIRONMENT_INITIAL_STATE = {
  device: {},
  error: null,
  location: {},
  onLine: true,
  stage: {},
  visibility: null,
}

export const environmentSet = () => ({
  payload: null,
  type: ENVIRONMENT_SET,
})

export const environmentSetError = (payload) => ({
  payload,
  type: ENVIRONMENT_SET_ERROR,
})

export const environmentSetFulfilled = () => ({
  payload: null,
  type: ENVIRONMENT_SET_FULFILLED,
})

export const environmentSetCanceled = () => ({
  payload: null,
  type: ENVIRONMENT_SET_CANCELED,
})

export const environmentDeviceSet = () => ({
  payload: null,
  type: ENVIRONMENT_DEVICE_SET,
})

export const environmentDeviceSetFulfilled = (device) => ({
  payload: {
    device,
  },
  type: ENVIRONMENT_DEVICE_SET_FULFILLED,
})

export const environmentDeviceSetCanceled = () => ({
  payload: null,
  type: ENVIRONMENT_DEVICE_SET_CANCELED,
})

export const environmentLocationSet = () => ({
  payload: {
    askAgain: true,
    asking: !!navigator?.geolocation?.getCurrentPosition,
    available: !!navigator?.geolocation?.getCurrentPosition,
    msg: null,
  },
  type: ENVIRONMENT_LOCATION_SET,
})

export const environmentLocationSetFulfilled = (payload) => ({
  payload,
  type: ENVIRONMENT_LOCATION_SET_FULFILLED,
})

export const environmentLocationSetCanceled = () => ({
  payload: null,
  type: ENVIRONMENT_LOCATION_SET_CANCELED,
})

export const environmentStageSet = () => ({
  payload: {
    stage: null,
  },
  type: ENVIRONMENT_STAGE_SET,
})

export const environmentStageSetFulfilled = (stage) => ({
  payload: {
    stage,
  },
  type: ENVIRONMENT_STAGE_SET_FULFILLED,
})

export const environmentStageSetCanceled = () => ({
  payload: {
    stage: null,
  },
  type: ENVIRONMENT_STAGE_SET_CANCELED,
})

export const environmentVisibilitySet = (visibility) => ({
  payload: visibility,
  type: ENVIRONMENT_VISIBILITY_SET,
})

export const environmentOnlineSet = (onLine) => ({
  payload: onLine,
  type: ENVIRONMENT_ONLINE_SET,
})

let lastUserAgent = null
export const environmentSetEpic = (action$) =>
  action$.pipe(
    ofType(ENVIRONMENT_SET),
    debounceTime(400),
    concatMap(() =>
      of(environmentDeviceSet(), environmentStageSet(), environmentSetFulfilled()).pipe(
        takeUntil(action$.pipe(ofType(ENVIRONMENT_SET, ENVIRONMENT_SET_CANCELED))),
        catchError((error) => of(environmentSetError(error))),
      ),
    ),
  )

const toggleBodyClassName = (className, add, reset = false) => {
  if (reset) {
    const userIn = document.body.classList.contains('userIn')
    const widget = document.body.classList.contains('widget')
    const noHeader = document.body.classList.contains('noHeader')
    document.body.className = ''
    if (userIn) {
      document.body.classList.add('userIn')
    }
    if (widget) {
      document.body.classList.add('widget')
    }
    if (noHeader) {
      document.body.classList.add('noHeader')
    }
  }
  if (add) {
    document.body.classList.add(className)
  } else {
    document.body.classList.remove(className)
  }
}

export const environmentDeviceSetEpic = (action$) =>
  action$.pipe(
    ofType(ENVIRONMENT_DEVICE_SET),
    filter(() => lastUserAgent !== window.navigator.userAgent),
    mergeMap(() => {
      lastUserAgent = window.navigator.userAgent
      const mobileDetect = new MobileDetect(lastUserAgent)
      const device = {
        mobile: !!mobileDetect.mobile(),
        os: mobileDetect.os() || null,
        phone: !!mobileDetect.phone(),
        tablet: !!mobileDetect.tablet(),
      }
      toggleBodyClassName('mobile', !!device.mobile, true)
      toggleBodyClassName('tablet', !!device.tablet)
      toggleBodyClassName('phone', !!device.phone)
      if (device.os) {
        toggleBodyClassName(device.os, true)
      }

      return of(environmentDeviceSetFulfilled(device)).pipe(
        takeUntil(action$.pipe(ofType(ENVIRONMENT_DEVICE_SET, ENVIRONMENT_DEVICE_SET_CANCELED))),
        catchError((error) => of(environmentSetError(error))),
      )
    }),
  )

export const environmentStageSetEpic = (action$) =>
  action$.pipe(
    ofType(ENVIRONMENT_STAGE_SET),
    mergeMap(() => {
      const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
      const small = width <= 800
      const little = width <= 640
      const tiny = width <= 480
      const mini = width <= 320

      toggleBodyClassName('small', small)
      toggleBodyClassName('little', little)
      toggleBodyClassName('tiny', tiny)
      toggleBodyClassName('mini', mini)

      return of(
        environmentStageSetFulfilled({
          height,
          little,
          mini,
          screen: {
            height: window.screen.height,
            width: window.screen.width,
          },
          small,
          tiny,
          width,
        }),
      ).pipe(
        takeUntil(action$.pipe(ofType(ENVIRONMENT_STAGE_SET, ENVIRONMENT_STAGE_SET_CANCELED))),
        catchError((error) => of(environmentSetError(error))),
      )
    }),
  )

// export const environmentLocationSetEpic = (action$, state$) => action$
//   .ofType(ENVIRONMENT_LOCATION_SET)
//   .mergeMap(() => {
//     const { environment } = state$.value
//     const timestamp = storage.get('auth.location')
//     const location = {
//       askAgain:
//           !timestamp
//           || environment.location.askAgain
//           || environment.location.askAgain === undefined,
//       asking: !!get(navigator, 'geolocation.getCurrentPosition'),
//       available: !!get(navigator, 'geolocation.getCurrentPosition'),
//       msg: null,
//       timestamp,
//     }
//
//     const actions = [Observable.of(environmentLocationSetFulfilled(location))]
//     if (location.available) {
//       actions.unshift(
//         Observable.create((observer) => {
//           const onSuccess = (position) => {
//             storage.set('auth.location', position.timestamp || 1, true)
//
//             const newLocation = {
//               accuracy: position.coords.accuracy,
//               altitude: position.coords.altitude,
//               altitudeAccuracy: position.coords.altitudeAccuracy,
//               askAgain: false,
//               asking: false,
//               available: true,
//               latitude: position.coords.latitude,
//               longitude: position.coords.longitude,
//               msg: null,
//               timestamp: position.timestamp || 1,
//             }
//
//             observer.next(environmentLocationSetFulfilled(newLocation))
//           }
//
//           const onError = (error) => {
//             const newLocation = { ...location }
//             newLocation.asking = false
//             switch (error.code) {
//               case 1: // PERMISSION_DENIED:
//                 newLocation.askAgain = true
//                 newLocation.available = true
//                 newLocation.msg = 'Acceso denegado'
//                 newLocation.timestamp = null
//                 break
//               case 2: // POSITION_UNAVAILABLE:
//                 newLocation.askAgain = false
//                 newLocation.available = false
//                 newLocation.msg = 'No es posible obtener'
//                 newLocation.timestamp = null
//                 break
//               case 3: // TIMEOUT
//                 newLocation.askAgain = true
//                 newLocation.available = true
//                 newLocation.msg = 'Demasido tiempo'
//                 break
//               default:
//                 newLocation.askAgain = false
//                 newLocation.available = true
//                 newLocation.msg = 'Desconocido'
//                 newLocation.timestamp = null
//             }
//             observer.error(newLocation)
//           }
//
//           const options = {
//             enableHighAccuracy: true,
//             timeout: Infinity,
//             maximumAge: 0,
//           }
//
//           navigator.geolocation.getCurrentPosition(onSuccess, onError, options)
//           return Function.prototype
//         }),
//       )
//     }
//     return Observable.concat(...actions)
//   })
//   .takeUntil(action$.ofType(ENVIRONMENT_LOCATION_SET_CANCELED))
//   .catch(payload => Observable.of({
//     type: ENVIRONMENT_LOCATION_SET_ERROR,
//     payload,
//   }))
